import type { IconName } from "../components/AppIcon";

/**
 * Module catalog rendered in the brochure overlay (Phase 4) and — when shipped —
 * the per-module pages under /brochure/<slug> (Phase 6 owns those).
 *
 * Keep this list ordered by the user journey, not alphabetically. Attendance and
 * Leaves are the first things most HR teams want to see; Tournament/Projects sit
 * at the end because they're discovery surfaces.
 *
 * Icons must exist in lucide-react (verify via AppIcon's IconName union).
 * Accent classes follow the muted-tint convention used elsewhere in the app:
 * a soft bg-* paired with a slightly stronger text-* / ring-* so the cards
 * read as a coherent set rather than a clown-car of saturated tiles.
 */

export type ModuleSlug =
  | "attendance"
  | "leaves"
  | "recruitment"
  | "impact"
  | "development"
  | "payroll"
  | "tournament"
  | "projects";

export interface BrochureModule {
  slug: ModuleSlug;
  /** i18n key — translated text appears in en/common.json + ar/common.json. */
  nameKey: string;
  /** One-sentence pitch (i18n key). Reads in flowing English/Arabic, not bullet copy. */
  pitchKey: string;
  /** lucide-react icon name — must be a real `IconName`. */
  iconName: IconName;
  /**
   * Tailwind accent tokens. We split bg / text / ring so the card surface,
   * the icon foreground, and the focus ring can each carry the tone without
   * tripping over each other.
   */
  accent: {
    surface: string; // soft tinted background for the icon chip
    iconText: string; // icon foreground colour
    ring: string; // hover/focus ring colour
  };
}

export const BROCHURE_MODULES: BrochureModule[] = [
  {
    slug: "attendance",
    nameKey: "brochure.module.attendance.name",
    pitchKey: "brochure.module.attendance.pitch",
    iconName: "Clock",
    accent: {
      surface: "bg-indigo-50",
      iconText: "text-indigo-600",
      ring: "hover:ring-indigo-300/60",
    },
  },
  {
    slug: "leaves",
    nameKey: "brochure.module.leaves.name",
    pitchKey: "brochure.module.leaves.pitch",
    iconName: "Calendar",
    accent: {
      surface: "bg-amber-50",
      iconText: "text-amber-700",
      ring: "hover:ring-amber-300/60",
    },
  },
  {
    slug: "recruitment",
    nameKey: "brochure.module.recruitment.name",
    pitchKey: "brochure.module.recruitment.pitch",
    iconName: "UserPlus",
    accent: {
      surface: "bg-sky-50",
      iconText: "text-sky-600",
      ring: "hover:ring-sky-300/60",
    },
  },
  {
    slug: "impact",
    nameKey: "brochure.module.impact.name",
    pitchKey: "brochure.module.impact.pitch",
    iconName: "Sparkles",
    accent: {
      surface: "bg-rose-50",
      iconText: "text-rose-600",
      ring: "hover:ring-rose-300/60",
    },
  },
  {
    slug: "development",
    nameKey: "brochure.module.development.name",
    pitchKey: "brochure.module.development.pitch",
    iconName: "TrendingUp",
    accent: {
      surface: "bg-emerald-50",
      iconText: "text-emerald-700",
      ring: "hover:ring-emerald-300/60",
    },
  },
  {
    slug: "payroll",
    nameKey: "brochure.module.payroll.name",
    pitchKey: "brochure.module.payroll.pitch",
    iconName: "Wallet",
    accent: {
      surface: "bg-violet-50",
      iconText: "text-violet-700",
      ring: "hover:ring-violet-300/60",
    },
  },
  {
    slug: "tournament",
    nameKey: "brochure.module.tournament.name",
    pitchKey: "brochure.module.tournament.pitch",
    iconName: "Trophy",
    accent: {
      surface: "bg-orange-50",
      iconText: "text-orange-600",
      ring: "hover:ring-orange-300/60",
    },
  },
  {
    slug: "projects",
    nameKey: "brochure.module.projects.name",
    pitchKey: "brochure.module.projects.pitch",
    iconName: "Layers",
    accent: {
      surface: "bg-teal-50",
      iconText: "text-teal-700",
      ring: "hover:ring-teal-300/60",
    },
  },
];
